import React, { useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { fetchMovies } from '../redux/slices/moviesSlice';
import apiConfig from '../config/apiConfig';

const ImportMoviesModal = ({ isOpen, onClose }) => {
  console.log("ImportMoviesModal renderizado con isOpen:", isOpen);
  
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importStep, setImportStep] = useState(1); // 1: Selección, 2: Vista previa, 3: Confirmación

  const dispatch = useDispatch();

  // Usar la misma configuración de API_BASE_URL que en moviesSlice
  const API_URL = apiConfig.base || process.env.REACT_APP_API_URL || "http://localhost:4000";
  console.log("ImportMoviesModal - API_URL:", API_URL);

  const getToken = () => {
    return localStorage.getItem("token");
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && selectedFile.name.split('.').pop().toLowerCase() !== 'csv') {
        setError('Por favor selecciona un archivo CSV válido');
        setFile(null);
        setFileName('');
        return;
      }
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setError('');
    }
  };

  const handlePreviewFile = async () => {
    if (!file) {
      setError('Por favor selecciona un archivo CSV');
      return;
    }

    setLoading(true);
    setError('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = getToken();
      const response = await axios.post(`${API_URL}/api/v1/movies/preview-csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setPreviewData(response.data);
        setImportStep(2); // Avanzar a la vista previa
      } else {
        setError(response.data.message || 'Error al procesar el archivo');
      }
    } catch (error) {
      console.error('Error previsualizando CSV:', error);
      setError(error.response?.data?.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleImportMovies = async () => {
    if (!previewData || !previewData.data || previewData.data.length === 0) {
      setError('No hay películas para importar');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = getToken();
      const response = await axios.post(`${API_URL}/api/v1/movies/import-csv`, 
        { movies: previewData.data },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setImportStep(3); // Avanzar a la confirmación
        // Refrescar lista de películas
        dispatch(fetchMovies());
      } else {
        setError(response.data.message || 'Error al importar las películas');
      }
    } catch (error) {
      console.error('Error importando películas:', error);
      setError(error.response?.data?.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setFile(null);
    setFileName('');
    setPreviewData(null);
    setError('');
    setImportStep(1);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) {
    console.log("ImportMoviesModal no se muestra porque isOpen es:", isOpen);
    return null;
  }

  console.log("ImportMoviesModal - Intentando renderizar el modal completo");

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999]" 
      style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0}}
    >
       {console.log("ImportMoviesModal - Primer div renderizado")}
      <div 
        className="bg-gray-800 text-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        style={{position: 'relative', zIndex: 10000}}
      >
      {console.log("ImportMoviesModal - Segundo div renderizado")}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">
            {importStep === 1 && "Importar Películas desde CSV"}
            {importStep === 2 && "Vista Previa de Películas"}
            {importStep === 3 && "Importación Exitosa"}
          </h2>
          <button 
            onClick={handleClose}
            className="text-gray-400 hover:text-white"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
            </svg>
          </button>
        </div>

        {error && (
          <div className="bg-red-900/50 border border-red-400 text-red-300 p-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {importStep === 1 && (
          <>
            <div className="mb-6">
              <p className="mb-4">Sube un archivo CSV con las siguientes columnas:</p>
              <div className="bg-gray-700 p-4 rounded-lg mb-4 text-sm overflow-x-auto">
                <code>name, genre, description, banner, poster, srtUrl, movieUrl, fileSize, mimeType, runtime</code>
              </div>
              <p>Los campos numéricos (fileSize, runtime) deben ser enteros.</p>
            </div>

            <div className="flex items-center justify-center w-full mb-6">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-700 hover:bg-gray-600">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <svg className="w-10 h-10 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                  </svg>
                  {fileName ? (
                    <p className="text-sm text-gray-300">{fileName}</p>
                  ) : (
                    <>
                      <p className="mb-2 text-sm text-gray-300">Haz clic para seleccionar un archivo CSV</p>
                      <p className="text-xs text-gray-400">CSV (max. 5MB)</p>
                    </>
                  )}
                </div>
                <input 
                  type="file" 
                  className="hidden" 
                  accept=".csv" 
                  onChange={handleFileChange}
                />
              </label>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={handleClose}
                className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-lg flex-1 sm:flex-none sm:px-6"
              >
                Cancelar
              </button>
              <button
                onClick={handlePreviewFile}
                disabled={!file || loading}
                className={`bg-blue-500 text-white p-2 rounded-lg flex-1 sm:flex-none sm:px-6 ${!file || loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'}`}
              >
                {loading ? 'Procesando...' : 'Vista Previa'}
              </button>
            </div>
          </>
        )}

        {importStep === 2 && previewData && (
          <>
            <div className="mb-4">
              <p className="text-green-400 mb-2">{previewData.message}</p>
              <p>Se han detectado {previewData.data.length} películas. Revisa que los datos sean correctos antes de importar.</p>
            </div>
            
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full bg-gray-900 rounded-lg overflow-hidden">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left">#</th>
                    <th className="px-4 py-2 text-left">Nombre</th>
                    <th className="px-4 py-2 text-left">Género</th>
                    <th className="px-4 py-2 text-left">Duración</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {previewData.data.slice(0, 10).map((movie, index) => (
                    <tr key={`preview-movie-${index}`} className="hover:bg-gray-800">
                      <td className="px-4 py-2">{index + 1}</td>
                      <td className="px-4 py-2">{movie.name}</td>
                      <td className="px-4 py-2">{movie.genre}</td>
                      <td className="px-4 py-2">{movie.runtime} min</td>
                    </tr>
                  ))}
                  {previewData.data.length > 10 && (
                    <tr>
                      <td colSpan="4" className="px-4 py-2 text-center text-gray-400">
                        ... y {previewData.data.length - 10} películas más
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setImportStep(1)}
                className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-lg px-6"
              >
                Atrás
              </button>
              <button
                onClick={handleImportMovies}
                disabled={loading}
                className={`bg-blue-500 text-white p-2 rounded-lg px-6 ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'}`}
              >
                {loading ? 'Importando...' : 'Importar Películas'}
              </button>
            </div>
          </>
        )}

        {importStep === 3 && (
          <>
            <div className="text-center py-6">
              <div className="mx-auto w-16 h-16 mb-4 text-green-400">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-medium mb-2 text-green-400">¡Importación Exitosa!</h3>
              <p className="text-gray-300 mb-6">
                Se han importado {previewData ? previewData.data.length : 0} películas correctamente.
              </p>
              <button
                onClick={handleClose}
                className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-6 rounded-lg"
              >
                Cerrar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ImportMoviesModal;
